import { forEach } from 'lodash';
import moment from 'moment';

import { Appointment } from './Appointment.model';
import { Disease } from './Disease.model';
import {
  DeviceDataView,
  ExamData,
  GradingDetail,
  GradingResults,
  ResultJson,
} from './GalenData/DeviceDataView.model';
import { OrderData } from './GalenData/Order.model';
import { Gender } from './Gender.model';
import { Grading } from './Grading.model';
import { ModelEnum } from './Model.model';
import { ReportStatus } from './ReportStatus.model';
import { ReportTemplate } from './ReportTemplate.model';
import { Result } from './Result.model';
import { Segmentation } from './Segmentation.model';
import { SegmentationType } from './SegmentationType.model';

export interface SessionHistory {
  PracticeUserId: string;
  UserFullname?: string;
  UserEmail?: string;
  SavedTime: Date;
  LeftEyeResultJson: ResultJson;
  RightEyeResultJson: ResultJson;
}

export interface SessionBase {
  iid: string;
  username: string;

  id: string;
  firstName: string;
  lastName: string;
  age?: number;
  gender?: Gender;
  dob?: string;

  date: string; // YYYY-MM-DD
  time: string; // HH:mm:ss

  leftImageFile?: string;
  rightImageFile?: string;

  graded: boolean;

  leftResults?: Result[];
  rightResults?: Result[];

  leftSegmentations?: Segmentation[];
  rightSegmentations?: Segmentation[];

  notes?: string;
  appointment?: Appointment;

  createdAt: Date;

  leftEyeDilatedTime?: Date;
  rightEyeDilatedTime?: Date;

  completedTime?: Date;
  completedReason?: CompletedReasonEnum;

  leftImageFileName: string;
  rightImageFileName: string;

  template?: ReportTemplate;

  sessionId: string;
  practiceUserId: string;

  history?: SessionHistory[];
}

export interface Session extends SessionBase {
  retakeSessions?: SessionBase[];
  order?: OrderData;
  orderId?: string;
  patientUserId?: string;
}

export enum CompletedReasonEnum {
  END_SESSION = 'End Session',
  COMPLETED_EXAM = 'Complete Exam',
  AUTO_COMPLETE = 'Auto Complete',
}

export enum ExamStatusEnum {
  IN_PROGRESS = 'In Progress',
  EXAM_ENDED = 'Exam Ended',
  EXAM_SUCCESSFUL = 'Exam Successful',
  UPLOADING = 'Uploading',
  UPLOAD_FAILED = 'Upload Failed',
  QUEUED = 'Queued',
  INCOMPLETE = 'Incomplete',
}

export enum EyesPosition {
  LEFT = 'left',
  RIGHT = 'right',
  BOTH = 'both',
}

export const PatientInfoKeyMapOptain = {
  id: 'PID',
  firstName: 'First Name',
  lastName: 'Last Name',
  dob: 'DOB',
};

export const DilatedInformation = {
  leftEyeDilatedTime: 'Left Eye Dilated',
  rightEyeDilatedTime: 'Right Eye Dilated',
};

export const PatientInfoKeyMapAssure = {
  id: 'Patient ID',
  firstName: 'First Name',
  lastName: 'Last Name',
  age: 'Age',
  gender: 'Sex',
};

interface DiseaseHeatmaps {
  [Disease.AMD]?: string;
  [Disease.DR]?: string;
  [Disease.GLAUCOMA]?: string;
}

export const transformExamToSession = (
  exam: DeviceDataView<ExamData>,
  username?: string,
) => {
  const patient = exam.owner;

  const leftEyeResults = exam?.data?.LeftEyeResultJson?.value.results;
  const leftEyeHeatmaps = {
    [Disease.AMD]: exam?.data?.LeftEyeAmdHeatmap?.link,
    [Disease.DR]: exam?.data?.LeftEyeDrHeatmap?.link,
    [Disease.GLAUCOMA]: exam?.data?.LeftEyeGlaucomaHeatmap?.link,
  };

  const rightEyeResults = exam?.data?.RightEyeResultJson?.value.results;
  const rightEyeHeatmaps = {
    [Disease.AMD]: exam?.data?.RightEyeAmdHeatmap?.link,
    [Disease.DR]: exam?.data?.RightEyeDrHeatmap?.link,
    [Disease.GLAUCOMA]: exam?.data?.RightEyeGlaucomaHeatmap?.link,
  };

  const session: Session = {
    iid: exam.deviceDataId || '',
    username: username || '',

    id: patient?.patientId || '',
    firstName: patient?.firstName || '',
    lastName: patient?.lastName || '',
    patientUserId: patient?.userId || '',
    age: patient?.dateOfBirth
      ? moment(exam?.data?.ReportDatetime?.value).diff(
          patient.dateOfBirth,
          'years',
        )
      : undefined,
    gender: exam?.data?.Gender?.value || undefined,
    dob: patient?.dateOfBirth,

    date: moment(exam?.data?.ReportDatetime?.value).format('YYYY-MM-DD'),
    time: moment(exam?.data?.ReportDatetime?.value).format('HH:mm:ss'),

    leftImageFile: exam?.data?.LeftImage?.link,
    rightImageFile: exam?.data?.RightImage?.link,

    graded:
      exam?.data?.Status?.value === ReportStatus.COMPLETE ||
      exam?.data?.Status?.value === ReportStatus.FAILED,

    leftResults:
      exam?.data.Status?.value === ReportStatus.COMPLETE
        ? resolveResult(leftEyeResults, leftEyeHeatmaps)
        : [],
    rightResults:
      exam?.data.Status?.value === ReportStatus.COMPLETE
        ? resolveResult(rightEyeResults, rightEyeHeatmaps)
        : [],

    leftSegmentations:
      exam?.data.Status?.value === ReportStatus.COMPLETE
        ? [
            {
              type: SegmentationType.GRID,
              filename: exam.data.LeftEyeVesselSeg?.link || '',
              createdAt: new Date(),
            },
          ]
        : [],
    rightSegmentations:
      exam?.data.Status?.value === ReportStatus.COMPLETE
        ? [
            {
              type: SegmentationType.GRID,
              filename: exam.data.RightEyeVesselSeg?.link || '',
              createdAt: new Date(),
            },
          ]
        : [],

    notes: exam?.data?.Notes?.value,
    appointment: exam?.data?.AppointmentType?.value as Appointment,

    createdAt: moment(exam?.data?.ExamSubmissionDatetime?.value).toDate(),

    leftEyeDilatedTime: exam.data.LeftEyeDilatedTime?.value,
    rightEyeDilatedTime: exam.data.RightEyeDilatedTime?.value,

    completedTime: exam.data.CompletedTime?.value,
    completedReason: exam.data.CompletedReason?.value,

    leftImageFileName: exam?.data?.LeftImage?.value,
    rightImageFileName: exam?.data?.RightImage?.value,
    orderId: exam?.data?.OrderId?.value,

    sessionId: exam?.data?.SessionId?.value,

    practiceUserId: exam.data.PracticeUserId?.value,

    history: (exam.data.History?.value || []) as unknown as SessionHistory[],
  };

  return session;
};

const diseaseMapping: Record<string, Disease> = {
  [ModelEnum.DR_US]: Disease.DR,
  [ModelEnum.DR_EU]: Disease.DR,
  [ModelEnum.DR_EU_SMALL]: Disease.DR,

  [ModelEnum.AMD_EU]: Disease.AMD,
  [ModelEnum.AMD_EU_SMALL]: Disease.AMD,

  [ModelEnum.GLAUCOMA_EU]: Disease.GLAUCOMA,
  [ModelEnum.GLAUCOMA_EU_SMALL]: Disease.GLAUCOMA,

  [ModelEnum.CVD_EU]: Disease.WHOCVD_RISK,
  [ModelEnum.CVD_EU_SMALL]: Disease.WHOCVD_RISK,

  [ModelEnum.CVD_AU]: Disease.AUSCVD_RISK,
  [ModelEnum.CVD_AU_SMALL]: Disease.AUSCVD_RISK,

  [ModelEnum.VESSEL_SEGMENTATION]: Disease.VESSEL_SEGMENTATION,
};

// Define a mapping of grading results to Result configurations
const gradingResultMapping: Record<
  string,
  { grading: Grading; gradable: boolean; error?: string }
> = {
  [Grading.UNGRADEABLE]: { grading: Grading.EMPTY, gradable: false },
  [Grading.ERROR]: {
    grading: Grading.EMPTY,
    gradable: false,
    error: 'Error',
  },

  // TODO: FIND A BETTER WAY TO DO THIS
  // DR-US
  [Grading.NEGATIVE]: { grading: Grading.NEGATIVE, gradable: true },
  [Grading.MTMDR.toLowerCase()]: { grading: Grading.MTMDR, gradable: true },
  [Grading.VTDR.toLowerCase()]: { grading: Grading.VTDR, gradable: true },

  // Glaucoma
  [Grading.LOW_RISK]: { grading: Grading.LOW_RISK, gradable: true },
  [Grading.MODERATE_RISK]: { grading: Grading.MODERATE_RISK, gradable: true },
  [Grading.HIGH_RISK]: { grading: Grading.HIGH_RISK, gradable: true },

  // AMD
  [Grading.NEGATIVE_OR_EARLY]: {
    grading: Grading.NEGATIVE_OR_EARLY,
    gradable: true,
  },
  [Grading.INTERMEDIATE]: { grading: Grading.INTERMEDIATE, gradable: true },
  [Grading.LATE_DRY]: { grading: Grading.LATE_DRY, gradable: true },
  [Grading.LATE_WET]: { grading: Grading.LATE_WET, gradable: true },

  // DR-EU
  [Grading.NO_DR]: { grading: Grading.NO_DR, gradable: true },
  [Grading.MILD_NPDR]: { grading: Grading.MILD_NPDR, gradable: true },
  [Grading.MODERATE_NPDR]: { grading: Grading.MODERATE_NPDR, gradable: true },
  [Grading.SEVERE_NPDR]: { grading: Grading.SEVERE_NPDR, gradable: true },
  [Grading.PROLIFERATIVE_DR]: {
    grading: Grading.PROLIFERATIVE_DR,
    gradable: true,
  },
};

export const resolveResult = (
  results: GradingResults,
  heatmaps: DiseaseHeatmaps,
): Result[] => {
  const resultList: Result[] = [];

  forEach(results, (value, key) => {
    const disease = diseaseMapping[key];
    const results = resolveDiseaseResult(disease, value, heatmaps);
    resultList.push(...results);
  });

  return resultList;
};

const resolveDiseaseResult = (
  disease: Disease,
  value: GradingDetail | undefined,
  heatmaps: DiseaseHeatmaps,
): Result[] => {
  if (!value) {
    return [];
  }

  switch (disease) {
    case Disease.WHOCVD_RISK: {
      return resolveCvdResult(value, [
        Disease.WHOCVD_RISK,
        Disease.WHOCVD_RANK,
      ]);
    }

    case Disease.AUSCVD_RISK: {
      return resolveCvdResult(value, [
        Disease.AUSCVD_RISK,
        Disease.AUSCVD_RANK,
      ]);
    }

    case Disease.DR:
    case Disease.AMD:
    case Disease.GLAUCOMA: {
      return resolveEyeResult(disease, value, heatmaps);
    }

    default: {
      return [];
    }
  }
};

const resolveCvdResult = (value: GradingDetail, keys: Disease[]): Result[] => {
  let grading: Record<string, number> = {};

  try {
    grading = JSON.parse(value.grading);
  } catch {
    grading = {};
  }

  return keys.map((disease) => {
    const exists = disease in grading;

    // TODO: Disease.AUSCVD_RANK is not supported yet, return a default result:
    if (disease === Disease.AUSCVD_RANK && !exists) {
      return {
        disease,
        error: undefined,
        gradable: true,
        grading: 0,
        conf: 0,
        heatmapFile: undefined,
      };
    }

    return {
      disease,
      error: exists ? undefined : value.grading,
      gradable: exists,
      grading: exists ? grading[disease] : 0,
      conf: exists ? value.conf : 0,
      heatmapFile: undefined,
    };
  });
};

const resolveEyeResult = (
  disease: Disease.AMD | Disease.DR | Disease.GLAUCOMA,
  value: GradingDetail,
  heatmaps: DiseaseHeatmaps,
): Result[] => {
  const result = gradingResultMapping[value.grading.toLowerCase()];

  if (!result) {
    return [];
  }

  const { grading, gradable, error } = result;
  return [
    {
      disease,
      error,
      gradable,
      grading,
      conf: value.conf,
      heatmapFile: heatmaps[disease],
    },
  ];
};
